import React from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PressableScale from './PressableScale';

/**
 * IconAction: icon + label tile.
 * Props:
 * - icon: Feather icon name, label (string), onPress, disabled
 * - variant: 'quick' (66 high, centred, top aligned; stop screen quick actions, 4 per row)
 *   | 'tile' (92 high, left aligned; Home tools grid, 2 per row)
 * - badge (string|number): small count in the corner, e.g. open tickets
 * - accessibilityHint, style (use style={{ flex: 1 }} inside a row)
 */
const IconAction = ({
	icon,
	label,
	onPress,
	disabled = false,
	variant = 'quick',
	badge,
	accessibilityHint,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui } } = useTheme();
	const isTile = variant === 'tile';
	const hasBadge = badge !== undefined && badge !== null && badge !== '';

	return (
		<PressableScale
			onPress={disabled ? undefined : onPress}
			disabled={disabled}
			accessibilityRole="button"
			accessibilityLabel={hasBadge ? `${label}, ${badge}` : label}
			accessibilityHint={accessibilityHint}
			accessibilityState={{ disabled }}
			style={({ pressed }) => [
				styles.action,
				isTile ? styles.tile : styles.quick,
				disabled && styles.disabled,
				pressed && styles.pressed,
				style,
			]}
		>
			<Feather
				name={icon}
				size={isTile ? 24 : 22}
				color={disabled ? ui.colors.disabledText : ui.colors.accent}
			/>
			<Text
				style={[styles.label, isTile && styles.tileLabel, disabled && styles.labelDisabled]}
				numberOfLines={2}
			>
				{label}
			</Text>
			{hasBadge && (
				<View style={styles.badge}>
					<Text style={styles.badgeText}>{badge}</Text>
				</View>
			)}
		</PressableScale>
	);
};

const createStyles = ({ ui: { colors, radius, type, space } }) => ({
	action: {
		borderRadius: radius.card,
		backgroundColor: colors.card,
	},
	quick: {
		minHeight: 66,
		alignItems: 'center',
		// Top aligned so icons stay level when a narrow phone wraps one label to two lines.
		justifyContent: 'flex-start',
		gap: 6,
		paddingHorizontal: space.xs,
		paddingTop: space.md,
		paddingBottom: space.sm,
	},
	tile: {
		minHeight: 92,
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: 14,
		padding: 14,
		borderRadius: radius.hero,
		// Same surface as ListRow (My account, Warehouse...): tappable rows and tiles look alike.
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	// Faded so an unavailable action never reads as an available (or highlighted) tile.
	disabled: {
		backgroundColor: colors.disabledBg,
		opacity: 0.5,
	},
	pressed: {
		opacity: 0.8,
	},
	label: {
		...type.caption,
		color: colors.icon,
		textAlign: 'center',
	},
	tileLabel: {
		...type.bodySmallSemibold,
		color: colors.text,
		textAlign: 'left',
	},
	labelDisabled: {
		color: colors.disabledText,
	},
	badge: {
		position: 'absolute',
		top: space.sm,
		right: space.sm,
		minWidth: 20,
		height: 20,
		borderRadius: 10,
		paddingHorizontal: 6,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.primary,
	},
	badgeText: {
		...type.label,
		color: colors.onPrimary,
	},
});

export default IconAction;
